import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import subscriptionResources from '@/modules/subscriptions/subscription/resources'

import { SUB_SERVICE_ORDER_STATUS } from '../constants'
import { SUB_SERVICE_ORDER_STATUS_BY_ID } from '../constants'

import queries from './queries'
import SubscriptionListItem from './SubscriptionListItem'
import type { Subscription } from './types'

import './SubscriptionList.styles.scss'

const SubscriptionList: FC<{
  searchFilter: string
  statusFilter: number
}> = ({ statusFilter, searchFilter }) => {
  const translate = useTranslation()
  const { locale } = useAppContext()

  const { data: subscriptionList = [], isLoading } = useQuery(
    queries.subscriptionList(),
    subscriptionResources.getList
  )
  const stringMatcher = (item: Subscription) => [
    item.nameNew,
    item.employeeNumber,
    translate(`sub_service_order_status/${SUB_SERVICE_ORDER_STATUS_BY_ID[item.status]}`),
    item.phoneNo,
    item.employeeNumber,
    item.customerName,
    item.orgNo,
  ]

  const getFilteredList = () => {
    if (statusFilter === undefined || statusFilter === SUB_SERVICE_ORDER_STATUS.ALL)
      return subscriptionList.filter(getQueryFilter(searchFilter, stringMatcher))

    return subscriptionList.filter(
      subscription =>
        subscription.status === statusFilter &&
        getQueryFilter(searchFilter, stringMatcher)(subscription)
    )
  }
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof filteredList)[number]>()

  const filteredList = getFilteredList()

  const sortedList = getSortedList({ list: filteredList, querySort, querySortBy, locale })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 15)

  if (isLoading) return <LoaderCircle />

  if (!visibleList || visibleList.length === 0)
    return (
      <NoResults>
        <Translation id="No subscriptions found" />
      </NoResults>
    )

  return (
    <>
      <ResultCount count={filteredList.length} />

      <Table rowHeight="tall">
        <thead>
          <tr>
            <th> </th>
            <Th id="nameNew" onClick={handleSort}>
              <Translation id="user" />
            </Th>
            <Th id="phoneNo" onClick={handleSort}>
              <Translation id="phone number" />
            </Th>
            <Th id="customerName" onClick={handleSort}>
              <Translation id="company" />
            </Th>
            <Th id="status" onClick={handleSort}>
              <Translation id="status" />
            </Th>
            <Th id="activationDate" onClick={handleSort}>
              <Translation id="activation" />
            </Th>
            <Th id="created" onClick={handleSort}>
              <Translation id="created" />
            </Th>
            <Th id="updated" onClick={handleSort}>
              <Translation id="updated" />
            </Th>
            <th />
          </tr>
        </thead>

        <tbody>
          {visibleList.map(item => (
            <SubscriptionListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}

export default SubscriptionList
